import { defineComponent, ref, computed, onMounted, nextTick } from "vue";
import { TUIConferenceStatus } from "@tencentcloud/tuiroom-engine-js";
import "../../services/main.mjs";
import { roomService } from "../../services/roomService.mjs";
import logger from "../../utils/common/logger/index.mjs";
import { useI18n } from "../../locales/index.mjs";
import { isMobile } from "../../utils/environment.mjs";
import { convertSecondsToHMS, getUrlWithRoomId } from "../../utils/utils.mjs";
import "mitt";
import "../../services/manager/roomActionManager.mjs";
import "@tencentcloud/tui-core";
import useRoomInfo from "../RoomHeader/RoomInfo/useRoomInfoHooks.mjs";
import SvgIcon from "../common/base/SvgIcon.vue.mjs";
import Dialog from "../common/base/Dialog/index.mjs";
import EllipsisIcon from "../common/icons/EllipsisIcon.vue.mjs";
import ChevronRight from "../common/icons/ChevronRight.vue.mjs";
import ArrowDown from "../common/icons/ArrowDown.vue.mjs";
import LinkIcon from "../common/icons/LinkIcon.vue.mjs";
import SuccessIcon from "../common/icons/SuccessIcon.vue.mjs";
import WarningIcon from "../common/icons/WarningIcon.vue.mjs";
import TuiButton from "../common/base/Button.vue.mjs";
import "../../directives/vClickOutside.mjs";
import ScheduleConferencePanel from "./ScheduleConferencePanel/index.mjs";
import { useBasicStore } from "../../stores/basic.mjs";
import { storeToRefs } from "pinia";
import ConferenceDetail from "./ConferenceDetail.vue.mjs";
import InvitePanel from "./InvitePanel.vue.mjs";
import PanelContainer from "./PanelContainer.vue.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ScheduleRoomControl",
  props: {
    item: null,
    scheduleStartDate: null,
    scheduleEndDate: null,
    scheduleStartTime: null,
    scheduleEndTime: null
  },
  emits: ["join-conference", "show-more"],
  setup(__props, { emit }) {
    const props = __props;
    const basicStore = useBasicStore();
    const { userId, userName, isRoomLinkVisible } = storeToRefs(basicStore);
    const roomLinkConfig = roomService.getComponentConfig("RoomLink");
    const { t } = useI18n();
    const showMoreControl = ref(false);
    const dropdownClass = ref("down");
    const moreBtnRef = ref();
    const operateListRef = ref();
    const modifyDialogVisible = ref(false);
    const showRoomDetail = ref(false);
    const showRoomCancel = ref(false);
    const showRoomInvite = ref(false);
    const roomInfo = ref();
    const { onCopy } = useRoomInfo();
    const statusMap = {
      [TUIConferenceStatus.kConferenceStatusRunning]: {
        text: "Ongoing",
        className: "status-running"
      }
    };
    const getStatusTextAndClass = (status) => statusMap[status] || { text: "", className: "" };
    const isOwner = computed(
      () => props.item.basicRoomInfo.ownerId === roomService.basicStore.userId && props.item.status === TUIConferenceStatus.kConferenceStatusNotStarted
    );
    const isShowPassword = computed(() => {
      var _a;
      return !!((_a = roomInfo.value) == null ? void 0 : _a.password);
    });
    const viewDetails = computed(() => ({
      key: "roomDetails",
      title: t("view details"),
      visible: true,
      func: () => {
        showRoomDetail.value = true;
        showMoreControl.value = false;
      }
    }));
    const modifyRoom = computed(() => ({
      key: "modifyRoom",
      title: t("modify room"),
      visible: isOwner.value,
      func: () => {
        modifyDialogVisible.value = true;
        showMoreControl.value = false;
      }
    }));
    const cancelRoom = computed(() => ({
      key: "cancelRoom",
      title: t("cancel room"),
      visible: isOwner.value,
      func: () => {
        showRoomCancel.value = true;
        showMoreControl.value = false;
      }
    }));
    const moreControlList = computed(() => [
      viewDetails.value,
      modifyRoom.value,
      cancelRoom.value
    ]);
    const scheduleAttendeesInfo = computed(
      () => props.item.scheduleAttendees ? props.item.scheduleAttendees.map(
        (attendee) => attendee.userName || attendee.userId
      ).join("; ") : ""
    );
    const durationTime = computed(
      () => props.item.scheduleEndTime - props.item.scheduleStartTime
    );
    function getScheduleTime(timestamp) {
      const date = new Date(timestamp);
      const hours = `${date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()}`;
      const minutes = `${date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()}`;
      return `${hours}:${minutes}`;
    }
    const scheduleRoomDetailList = computed(() => {
      var _a;
      const { hours, minutes } = convertSecondsToHMS(
        props.item.scheduleEndTime - props.item.scheduleStartTime
      );
      return [
        {
          title: "Room Name",
          content: props.item.basicRoomInfo.roomName,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: true
        },
        {
          title: "Room ID",
          content: props.item.basicRoomInfo.roomId,
          isShowCopyIcon: true,
          status: null,
          isShowStatus: false,
          isVisible: true
        },
        {
          title: "Room Time",
          content: `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndDate} ${props.scheduleEndTime}`,
          isShowCopyIcon: false,
          status: props.item.status,
          isShowStatus: true,
          isVisible: !isMobile
        },
        {
          title: "Starting time",
          content: `${props.scheduleStartDate} ${props.scheduleStartTime}`,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: isMobile
        },
        {
          title: "Room duration",
          content: hours ? `${hours}${t("hours")}${minutes ? ` ${minutes}${t("minutes")}` : ""}` : `${minutes}${t("minutes")}`,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: isMobile
        },
        {
          title: "Room Type",
          content: `${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: true
        },
        {
          title: "Room Password",
          content: `${(_a = roomInfo.value) == null ? void 0 : _a.password}`,
          isShowCopyIcon: true,
          status: null,
          isShowStatus: false,
          isVisible: isShowPassword.value
        },
        {
          title: "Creator",
          content: props.item.basicRoomInfo.roomOwner,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: true
        },
        {
          title: "Participants",
          content: scheduleAttendeesInfo.value,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: true
        },
        {
          title: "Entry Time",
          content: `${props.scheduleStartDate}${props.scheduleStartTime}`,
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: props.item.status === TUIConferenceStatus.kConferenceStatusNone
        },
        {
          title: "Duration of participation",
          content: getScheduleTime(durationTime.value),
          isShowCopyIcon: false,
          status: null,
          isShowStatus: false,
          isVisible: props.item.status === TUIConferenceStatus.kConferenceStatusNone
        }
      ];
    });
    const scheduleInviteList = computed(() => {
      var _a;
      return [
        {
          title: "Room Name",
          content: props.item.basicRoomInfo.roomName,
          isShowCopyIcon: false,
          isVisible: true
        },
        {
          title: "Room Type",
          content: `${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
          isShowCopyIcon: false,
          isVisible: true
        },
        {
          title: "Room Time",
          content: !isMobile ? `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndDate} ${props.scheduleEndTime}` : `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndTime}`,
          isShowCopyIcon: false,
          isVisible: true
        },
        {
          title: "Room ID",
          content: props.item.basicRoomInfo.roomId,
          isShowCopyIcon: true,
          isVisible: true
        },
        {
          title: "Room Password",
          content: `${(_a = roomInfo.value) == null ? void 0 : _a.password}`,
          isShowCopyIcon: true,
          isVisible: isShowPassword.value
        },
        {
          title: "Room Link",
          content: getUrlWithRoomId(props.item.basicRoomInfo.roomId),
          isShowCopyIcon: isMobile,
          isVisible: isRoomLinkVisible.value && roomLinkConfig.visible
        }
      ];
    });
    function getSeatModeDisplay(isSeatEnabled) {
      return isSeatEnabled ? "On-stage Speaking Room" : "Free Speech Room";
    }
    async function toggleClickMoreBtn() {
      emit("show-more", { roomId: props.item.basicRoomInfo.roomId });
      if (!showMoreControl.value) {
        await handleDropDownPosition();
      }
      showMoreControl.value = !showMoreControl.value;
    }
    function handleCloseOperate() {
      showMoreControl.value = false;
    }
    async function handleDropDownPosition() {
      var _a, _b, _c;
      await nextTick();
      const { top, bottom } = ((_a = moreBtnRef.value) == null ? void 0 : _a.getBoundingClientRect()) || {};
      const { top: containerTop, bottom: containerBottom } = (_b = document.getElementById("scheduleRoomContainer")) == null ? void 0 : _b.getBoundingClientRect();
      if (!containerBottom || !containerTop) {
        return;
      }
      const bottomSize = containerBottom - bottom;
      const topSize = top - containerTop;
      let dropDownContainerHeight = 0;
      if (!showMoreControl.value) {
        operateListRef.value.style = "display:block;position:absolute;z-index:-1000";
        await nextTick();
        dropDownContainerHeight = operateListRef.value.offsetHeight;
        operateListRef.value.style = "";
      } else {
        dropDownContainerHeight = (_c = operateListRef.value) == null ? void 0 : _c.offsetHeight;
      }
      if (topSize < dropDownContainerHeight) {
        return;
      }
      if (bottomSize < dropDownContainerHeight) {
        dropdownClass.value = "up";
      }
    }
    const invitePanelShow = ref(false);
    const joinConference = () => {
      var _a;
      emit("join-conference", {
        roomId: (_a = props.item) == null ? void 0 : _a.basicRoomInfo.roomId,
        roomParam: {
          isOpenCamera: false,
          isOpenMicrophone: true
        }
      });
    };
    const toggleInvitePanelShow = () => {
      invitePanelShow.value = !invitePanelShow.value;
    };
    const showDetail = () => {
      if (!isMobile) return;
      emit("show-more", { roomId: props.item.basicRoomInfo.roomId });
      viewDetails.value.func();
    };
    function toggleInviteRoom() {
      if (showRoomDetail.value) {
        showRoomDetail.value = false;
      }
      showRoomInvite.value = !showRoomInvite.value;
    }
    function copyRoomIdAndRoomLink() {
      var _a;
      const invitationList = [
        `${props.item.basicRoomInfo.roomName}`,
        `${t("Room Type")}: ${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
        `${t("Room ID")}: ${props.item.basicRoomInfo.roomId}`
      ];
      if (isShowPassword.value) {
        invitationList.push(`${t("Room Password")}: ${(_a = roomInfo.value) == null ? void 0 : _a.password}`);
      }
      if (isRoomLinkVisible.value && roomLinkConfig.visible) {
        invitationList.push(
          `${t("Room Link")}: ${getUrlWithRoomId(props.item.basicRoomInfo.roomId)}`
        );
      }
      const invitation = invitationList.join("\n");
      onCopy(invitation);
    }
    async function handleCancelSchedule() {
      await roomService.scheduleConferenceManager.cancelConference({
        roomId: props.item.basicRoomInfo.roomId
      });
      showRoomCancel.value = false;
      showRoomDetail.value = false;
    }
    onMounted(async () => {
      try {
        const roomInfoParams = {
          roomId: props.item.basicRoomInfo.roomId,
          roomType: props.item.basicRoomInfo.roomType
        };
        roomInfo.value = await roomService.fetchRoomInfo(roomInfoParams);
      } catch (error) {
        logger.error("fetch roomInfo failed:", error);
      }
    });
    return { __sfc: true, basicStore, userId, userName, isRoomLinkVisible, roomLinkConfig, t, showMoreControl, dropdownClass, moreBtnRef, operateListRef, modifyDialogVisible, emit, showRoomDetail, showRoomCancel, showRoomInvite, roomInfo, props, onCopy, statusMap, getStatusTextAndClass, isOwner, isShowPassword, viewDetails, modifyRoom, cancelRoom, moreControlList, scheduleAttendeesInfo, durationTime, getScheduleTime, scheduleRoomDetailList, scheduleInviteList, getSeatModeDisplay, toggleClickMoreBtn, handleCloseOperate, handleDropDownPosition, invitePanelShow, joinConference, toggleInvitePanelShow, showDetail, toggleInviteRoom, copyRoomIdAndRoomLink, handleCancelSchedule, TUIConferenceStatus, SvgIcon, Dialog, EllipsisIcon, ChevronRight, ArrowDown, LinkIcon, SuccessIcon, WarningIcon, TuiButton, ScheduleConferencePanel, isMobile, ConferenceDetail, InvitePanel, PanelContainer };
  }
});
export {
  _sfc_main as default
};
